import type { TenantClinicRoute } from '../../types';
import {
  KEILOG_CLINIC_LOCATION_PATH,
  KEILOG_COMING_SOON_PATH,
  KEILOG_CORPORATE_INVOICE_DETAIL_PATH,
  KEILOG_CORPORATE_INVOICE_PATH,
  KEILOG_INSTALLMENT_PAYMENT_PATH,
  KEILOG_FRANCHISE_INVOICE_PATH,
  KEILOG_INSURER_PAYMENT_PATH,
  KEILOG_PAYMENT_VARIANCE_PATH,
  KEILOG_LOCATION_PAYMENT_PATH,
  KEILOG_CORPORATE_FACILITY_MASTER_PATH,
  KEILOG_CORPORATE_MASTER_PATH,
  KEILOG_DOCUMENT_SET_PATH,
  KEILOG_INSURER_INVOICE_PATH,
  KEILOG_MONTHLY_CONFIRM_PATH,
  KEILOG_PATIENT_REGISTRATION_ROUTE_PATH,
  KEILOG_RECEIPT_PRINT_PATH,
  KEILOG_TACK_SEAL_PATH,
  KEILOG_TACK_SEAL_PRINT_PATH,
  KEILOG_RECEIPT_STATUS_PATH,
  KEILOG_RECEIPT_DEADLINE_PATH,
  KEILOG_RECEIPT_LIST_PATH,
  KEILOG_MONTHLY_CLOSING_PATH,
  KEILOG_FB_ACCOUNTS_PATH,
  KEILOG_FB_OUTPUT_PATH,
  KEILOG_FIRM_BANKING_PATH,
  KEILOG_REZEPT_PROCESSING_PATH,
  KEILOG_INSURANCE_REVIEW_PATH,
  KEILOG_INSURER_MASTER_PATH,
  KEILOG_LONG_TERM_FREQUENTS_PATH,
  KEILOG_OFFICE_DATA_PATH,
  KEILOG_OUTCOME_REGISTRATION_PATH,
  KEILOG_PATIENT_APPROVAL_PATH,
  KEILOG_PATIENT_CAPTURE_PATH,
  KEILOG_PATIENT_DETAIL_PATH,
  KEILOG_CONSENT_MANAGEMENT_PATH,
  KEILOG_PATIENT_MANAGEMENT_PATH,
  KEILOG_QUESTIONNAIRE_PATH,
  KEILOG_RBAC_SETTINGS_PATH,
  KEILOG_RECEIPT_MANAGEMENT_PATH,
  KEILOG_RESULTS_MANAGEMENT_PATH,
  KEILOG_RESULTS_SUMMARY_PATH,
  KEILOG_RESULT_CHANGE_PATH,
  KEILOG_RESULT_VARIANCE_PATH,
  KEILOG_TREATMENT_RECORD_PATH,
  KEILOG_SALES_ANALYSIS_PATH,
  KEILOG_ACTIVE_USER_TREND_PATH,
  KEILOG_REPORT_DASHBOARD_PATH,
  KEILOG_SALES_FORECAST_PATH,
  KEILOG_FRANCHISE_SALES_PATH,
  KEILOG_DAILY_SALES_PATH,
  KEILOG_SCHEDULE_TOTAL_PATH,
  KEILOG_TREATMENT_REPORT_LIST_PATH,
  KEILOG_NOTICE_PATH,
  KEILOG_PROGRESS_REPORT_PC_PATH,
  KEILOG_TREATMENT_REPORT_PATH,
  KEILOG_USER_MASTER_CREATE_PATH,
  KEILOG_USER_MASTER_PATH,
  KEILOG_DAILY_REPORT_PATH,
  KEILOG_VISIT_RECORD_ENTRY_PATH,
  KEILOG_VISIT_SCHEDULE_PATH,
} from '../constants/paths';
import { CLINIC_MASTER_PATH } from '../pages/clinic-master/access';
import { keilogDesignGuidelineRoutes } from './designGuidelineRoutes';

/**
 * Route metadata stays lightweight. Every element is imported only after the
 * canonical KEiLOG route has matched, so the central tenant registry never
 * evaluates shell/page modules while it is still initializing.
 */
export const keilogClinicRoutes: TenantClinicRoute[] = [
  { path: 'hq-settings', component: () => import('./CompanyMasterRoute') },
  {
    path: 'main/*',
    component: () => import('./LegacyPrefixRedirect'),
  },
  {
    path: 'checkin/*',
    component: () => import('./LegacyPrefixRedirect'),
  },
  {
    path: `${KEILOG_COMING_SOON_PATH.slice(1)}/*`,
    component: () => import('../shared/guards/NotImplementedPage'),
  },
  {
    path: KEILOG_PATIENT_MANAGEMENT_PATH.slice(1),
    component: () => import('./PatientManagementRoute'),
  },
  {
    // 同意書管理 (SCR-050) — own path: core mounts /consents/treatment_period with a static import.
    path: KEILOG_CONSENT_MANAGEMENT_PATH.slice(1),
    component: () => import('./ConsentManagementRoute'),
  },
  {
    // 保険証確認・一覧 (SCR-053) — a path RA2N does not have, and no `ModuleLoader` mount point
    // to override, so it belongs here rather than in `componentOverrides.tsx`.
    path: KEILOG_INSURANCE_REVIEW_PATH.slice(1),
    component: () => import('./InsuranceReviewRoute'),
  },
  {
    // 利用者情報 (施術師 / P6-SP08) — search box and card on ONE screen, phone only.
    // The 利用者 rides in `?patientId=`, not in a path segment, so that the URL
    // can also mean 「no-one picked yet」 (see `KEILOG_PATIENT_DETAIL_QUERY_PARAM`).
    path: KEILOG_PATIENT_DETAIL_PATH.slice(1),
    component: () => import('./PatientDetailRoute'),
  },
  {
    path: `${KEILOG_PATIENT_CAPTURE_PATH.slice(1)}/:patientId`,
    component: () => import('./PatientCaptureRoute'),
  },
  {
    path: KEILOG_PATIENT_APPROVAL_PATH.slice(1),
    component: () => import('./PatientApprovalRoute'),
  },
  {
    path: KEILOG_RBAC_SETTINGS_PATH.slice(1),
    component: () => import('./RbacSettingsRoute'),
  },
  // 問診票 (SCR-056) — a path RA2N does not have, and no `ModuleLoader` mount
  // point to override, so it belongs here rather than in `componentOverrides`.
  {
    path: KEILOG_QUESTIONNAIRE_PATH.slice(1),
    component: () => import('./QuestionnaireRoute'),
  },
  // 施術報告書 (SCR-084) — a path RA2N does not have. Its own
  // `treatment-reports/:patientId/…` routes stay core's and answer a different
  // question: the reports of one 利用者, reached from that 利用者's screen.
  {
    path: KEILOG_TREATMENT_REPORT_PATH.slice(1),
    component: () => import('./TreatmentReportRoute'),
  },
  // 施術経過報告書 PC (SCR-084-003) — the 📈 tab beside 施術報告書; a path RA2N does not have.
  {
    path: KEILOG_PROGRESS_REPORT_PC_PATH.slice(1),
    component: () => import('./ProgressReportRoute'),
  },
  // お知らせ (SCR-192) — the 施術師 phone app's 🔔 tab; a path RA2N does not have.
  {
    path: KEILOG_NOTICE_PATH.slice(1),
    component: () => import('./NoticeRoute'),
  },
  {
    // 基幹店マスタ (SCR-001) — a path RA2N does not have, so it belongs here
    // rather than in `componentOverrides.tsx`.
    path: CLINIC_MASTER_PATH.slice(1),
    component: () => import('./ClinicMasterRoute'),
  },
  { path: 'clinics/init/create', component: () => import('./ClinicCreateRoute') },
  { path: 'clinics/init/:id/edit', component: () => import('./ClinicEditRoute') },
  {
    path: KEILOG_USER_MASTER_CREATE_PATH.slice(1),
    component: () => import('./UserAccountCreateRoute'),
  },
  {
    path: 'user-master/edit/:type/:id',
    component: () => import('./UserAccountEditRoute'),
  },
  // SCR-009 ユーザアカウント.
  {
    path: KEILOG_USER_MASTER_PATH.slice(1),
    component: () => import('./UserMasterRoute'),
  },
  {
    path: 'massagers/init/create',
    component: () => import('./TherapistCreateRoute'),
  },
  {
    path: 'massagers/init/:id/edit',
    component: () => import('./TherapistEditRoute'),
  },
  // SCR-086 施術報告書一覧 — a path RA2N does not have. Its own
  // `treatment-reports/{patientId}/list` is a child screen of one 利用者, so
  // there is nothing to override.
  {
    path: KEILOG_TREATMENT_REPORT_LIST_PATH.slice(1),
    component: () => import('./TreatmentReportListRoute'),
  },
  // SCR-137-001 書類セット — a path RA2N does not have.
  {
    path: KEILOG_DOCUMENT_SET_PATH.slice(1),
    component: () => import('./DocumentSetRoute'),
  },
  // SCR-133 総括票印刷 — a path RA2N does not have.
  {
    path: KEILOG_INSURER_INVOICE_PATH.slice(1),
    component: () => import('./InsurerInvoiceRoute'),
  },
  // SCR-136 レセプト印刷 — a path RA2N does not have.
  {
    path: KEILOG_RECEIPT_PRINT_PATH.slice(1),
    component: () => import('./ReceiptPrintRoute'),
  },
  // SCR-138 タックシール印刷 — a path RA2N does not have. The menu's `/tack-seal` hands over to the
  // address BD final 「Cách access」 gives the screen (`/keilog/tack-seal-print`).
  {
    path: KEILOG_TACK_SEAL_PATH.slice(1),
    component: () => import('./TackSealMenuRedirect'),
  },
  {
    path: KEILOG_TACK_SEAL_PRINT_PATH.slice(1),
    component: () => import('./TackSealRoute'),
  },
  // SCR-SET1 システム設定 › 利用者登録ルート — a path RA2N does not have.
  {
    path: KEILOG_PATIENT_REGISTRATION_ROUTE_PATH.slice(1),
    component: () => import('./PatientRegistrationRouteSettingRoute'),
  },
  {
    path: KEILOG_RECEIPT_STATUS_PATH.slice(1),
    component: () => import('./ReceiptStatusRoute'),
  },
  // SCR-082 実績締め管理: a path RA2N does not have.
  {
    path: KEILOG_RECEIPT_DEADLINE_PATH.slice(1),
    component: () => import('./ReceiptDeadlineRoute'),
  },
  // SCR-089 (BD SCR-402) レセプト状況: a path RA2N does not have.
  {
    path: KEILOG_RECEIPT_LIST_PATH.slice(1),
    component: () => import('./ReceiptListRoute'),
  },
  // SCR-404 月間締め明細: a path RA2N does not have.
  {
    path: KEILOG_MONTHLY_CLOSING_PATH.slice(1),
    component: () => import('./MonthlyClosingRoute'),
  },
  // SCR-123 FB出力(全銀): a path RA2N does not have.
  {
    path: KEILOG_FB_OUTPUT_PATH.slice(1),
    component: () => import('./FbOutputRoute'),
  },
  // SCR-024 口座設定 (Account settings): a path RA2N does not have.
  {
    path: KEILOG_FB_ACCOUNTS_PATH.slice(1),
    component: () => import('./FbAccountsRoute'),
  },
  // SCR-224 to SCR-227 ファームバンキング (Firm banking): a path RA2N does not have.
  {
    path: KEILOG_FIRM_BANKING_PATH.slice(1),
    component: () => import('./FirmBankingRoute'),
  },
  {
    path: KEILOG_CLINIC_LOCATION_PATH.slice(1),
    component: () => import('./ClinicLocationRoute'),
  },
  {
    path: `${KEILOG_CLINIC_LOCATION_PATH.slice(1)}/create`,
    component: () => import('./ClinicLocationCreateRoute'),
  },
  {
    path: `${KEILOG_CLINIC_LOCATION_PATH.slice(1)}/:id/edit`,
    component: () => import('./ClinicLocationEditRoute'),
  },
  {
    path: `${KEILOG_CLINIC_LOCATION_PATH.slice(1)}/:id`,
    component: () => import('./ClinicLocationRoute'),
  },
  // SCR-073-001 実績管理 — a path RA2N does not have. Its own check result
  // (`rezepts/visit_check_ready/…`) is a different screen and stays core's.
  {
    path: KEILOG_RESULTS_MANAGEMENT_PATH.slice(1),
    component: () => import('./ResultsManagementRoute'),
  },
  // SCR-071 施術録管理 — a path RA2N does not have; the six roles reach it by the menu (FIX-PLAN Q-A).
  {
    path: KEILOG_TREATMENT_RECORD_PATH.slice(1),
    component: () => import('./TreatmentRecordRoute'),
  },
  // SCR-088 レセプト処理 — a path RA2N does not have; core's `rezepts/init` stays as it is (FIX-PLAN Q-A).
  {
    path: KEILOG_REZEPT_PROCESSING_PATH.slice(1),
    component: () => import('./RezeptProcessingRoute'),
  },
  // SCR-074 実績変更管理 — a path RA2N does not have (BD 「Access」).
  {
    path: KEILOG_RESULT_CHANGE_PATH.slice(1),
    component: () => import('./ResultChangeRoute'),
  },
  // SCR-403 月次確定 — a path RA2N does not have (BD Cách access).
  {
    path: KEILOG_MONTHLY_CONFIRM_PATH.slice(1),
    component: () => import('./MonthlyConfirmRoute'),
  },
  // SCR-077 実績差異一覧 — a path RA2N does not have.
  {
    path: KEILOG_RESULT_VARIANCE_PATH.slice(1),
    component: () => import('./ResultVarianceRoute'),
  },
  // SCR-078 施術人数・売上合計 — a path RA2N does not have (BD 「Access」).
  {
    path: KEILOG_RESULTS_SUMMARY_PATH.slice(1),
    component: () => import('./ResultsSummaryRoute'),
  },
  // SCR-065 訪問スケジュール — a tenant path. RA2N's `visits/schedule` is its week
  // calendar (SCR-061's counterpart), not this screen, so it is not overridden.
  {
    path: KEILOG_VISIT_SCHEDULE_PATH.slice(1),
    component: () => import('./VisitScheduleRoute'),
  },
  // SCR-067-001 施術記録入力 — a screen of its own, as the concept draws it. The
  // bare path is the day's first visit; the two segments name one.
  {
    path: KEILOG_VISIT_RECORD_ENTRY_PATH.slice(1),
    component: () => import('./VisitRecordRoute'),
  },
  {
    path: `${KEILOG_VISIT_RECORD_ENTRY_PATH.slice(1)}/:visitKey`,
    component: () => import('./VisitRecordRoute'),
  },
  // SCR-072 施術日報 — the day as a worklist, and where 施術記録入力 opens from in
  // the concept. Reads its own endpoint; no path of RA2N's own is taken over.
  {
    path: KEILOG_DAILY_REPORT_PATH.slice(1),
    component: () => import('./DailyReportRoute'),
  },
  // SCR-129 法人請求書発行（詳細） — 入金精算 › 🧾 法人請求詳細. Declared before the retired list path
  // so the longer path is matched first.
  {
    path: KEILOG_CORPORATE_INVOICE_DETAIL_PATH.slice(1),
    component: () => import('./CorporateInvoiceDetailRoute'),
  },
  // The retired SCR-128-001 list: old links redirect to the detail.
  {
    path: KEILOG_CORPORATE_INVOICE_PATH.slice(1),
    component: () => import('./CorporateInvoiceRoute'),
  },
  // SCR-125 分割弁済管理 — a path RA2N does not have.
  {
    path: KEILOG_INSTALLMENT_PAYMENT_PATH.slice(1),
    component: () => import('./InstallmentPaymentRoute'),
  },
  // SCR-127 基幹店請求書 / 基幹店請求詳細 — a path RA2N does not have.
  {
    path: KEILOG_FRANCHISE_INVOICE_PATH.slice(1),
    component: () => import('./FranchiseInvoiceRoute'),
  },
  // SCR-113-001 保険者別入金 (本部管理) and SCR-113-003 入金状況 (FCオーナー) — one path RA2N does not have.
  {
    path: KEILOG_INSURER_PAYMENT_PATH.slice(1),
    component: () => import('./PaymentStatusRoute'),
  },
  // SCR-115 入金差異 — a path RA2N does not have.
  {
    path: KEILOG_PAYMENT_VARIANCE_PATH.slice(1),
    component: () => import('./PaymentVarianceRoute'),
  },
  // SCR-402 拠点別入金 — a path RA2N does not have.
  {
    path: KEILOG_LOCATION_PAYMENT_PATH.slice(1),
    component: () => import('./LocationPaymentRoute'),
  },
  // SCR-029 法人マスタ管理 — a path RA2N does not have.
  {
    path: KEILOG_CORPORATE_MASTER_PATH.slice(1),
    component: () => import('./CorporateMasterRoute'),
  },
  // SCR-030 法人 新規登録 — the drawer over 法人マスタ管理, opened by its ＋ 新規法人 button.
  { path: `${KEILOG_CORPORATE_MASTER_PATH.slice(1)}/create`, component: () => import('./CorporateCreateRoute') },
  // SCR-029-002 法人 drawer; 💾 保存 of 備考（社内） is SCR-206.
  { path: `${KEILOG_CORPORATE_MASTER_PATH.slice(1)}/:id/edit`, component: () => import('./CorporateEditRoute') },
  // SCR-031 法人施設マスタ — the 🏗 法人施設 tab of 法人マスタ; 新規作成 / 編集 are drawers on the list.
  {
    path: KEILOG_CORPORATE_FACILITY_MASTER_PATH.slice(1),
    component: () => import('./CorporateFacilityMasterRoute'),
  },
  // 転帰登録 (SCR-221) — a path RA2N does not have; its 転帰 is a modal on `rezepts/init`, which
  // stays where it is and no longer draws the button (`_spec-レセプト一覧-設計.md:131`).
  {
    path: KEILOG_OUTCOME_REGISTRATION_PATH.slice(1),
    component: () => import('./OutcomeRegistrationRoute'),
  },
  // SCR-061 基本スケジュール — a path RA2N does not have (FIX-PLAN Q-C). The literal matches
  // `pages/base-schedule/utils/access.ts` BASE_SCHEDULE_PATH and `app/Tenants/Keilog/Config/rbac.php`.
  { path: 'base-schedule', component: () => import('./BaseScheduleRoute') },
  // SCR-061-003 スケジュール管理 › トータル — a path RA2N does not have. 週間表示 (SCR-061-002) keeps RA2N's
  // `visits/schedule` and arrives through `componentOverrides.tsx` instead.
  { path: KEILOG_SCHEDULE_TOTAL_PATH.slice(1), component: () => import('./ScheduleTotalRoute') },
  // 領収書・請求書 (SCR-135) — case 3: core's rezepts/invoices_init stays, unlisted in the menu.
  {
    path: KEILOG_RECEIPT_MANAGEMENT_PATH.slice(1),
    component: () => import('./ReceiptManagementRoute'),
  },
  { path: 'insurance-switch', component: () => import('./InsuranceSwitchRoute') },
  // 医師 新規登録 / 編集 have no route: they open only from the list (RahDrawerWorkspace LIST_ONLY_FORMS).
  { path: 'doctor-master', component: () => import('./DoctorMasterRoute') },
  // SCR-034 事業所リスト — a path RA2N does not have (ca 2).
  { path: 'business-destinations', component: () => import('./BusinessDestinationMasterRoute') },
  // ケアマネマスタ — the 👩‍⚕ tab of the オーナー / スタッフ 現場マスタ bar; a path RA2N does not have.
  { path: 'care-managers', component: () => import('./CareManagerMasterRoute') },
  // 同一建物マスタ (FN11) — the 🏘 tab of the 現場マスタ bar; a path RA2N does not have.
  // SCR-020 保険診療料金 (Insurance treatment fee) — a tenant path: core's `medical_expense_prices/init`
  // is a static element with no seam. 新規登録 / 編集 open over the list; they have no route.
  { path: 'insurance-fee-master', component: () => import('./InsuranceFeeMasterRoute') },
  { path: 'same-building-master', component: () => import('./SameBuildingMasterRoute') },
  { path: 'same-building-master/create', component: () => import('./SameBuildingCreateRoute') },
  { path: 'same-building-master/:id/edit', component: () => import('./SameBuildingEditRoute') },
  // 医療機関マスタ (No.25–27) — a tenant path; core's `hospitals/init` is a static element with no seam.
  // 医療機関 編集 has no route: it opens only from the list (RahDrawerWorkspace LIST_ONLY_FORMS). 新規登録
  // keeps its typed URL (BD SCR-213 row 6).
  { path: 'hospital-master', component: () => import('./HospitalMasterRoute') },
  { path: 'hospital-master/create', component: () => import('./HospitalCreateRoute') },
  // 自由診療料金 (SCR-018) — a tenant path; core's `free_medical/init` is a static element with no seam.
  // Its four drawers have no route of their own: they open only from the list (RahDrawerWorkspace
  // LIST_ONLY_FORMS), over the tab the list is on.
  { path: 'free-treatment-price', component: () => import('./FreeTreatmentPriceRoute') },
  { path: 'tax-rate-master', component: () => import('./TaxRateMasterRoute') },
  // SCR-160 お知らせ管理 — a tenant path; core's `admin/informations` belongs to the admin portal.
  { path: 'information-master', component: () => import('./InformationMasterRoute') },
  // SCR-162 通知ログ — システム設定 › 設定 › 通知ログ, read only.
  { path: 'notification-log', component: () => import('./NotificationLogRoute') },
  // SCR-162_01 受信通知 — 一般 › 🔔 通知 of オーナー / スタッフ, the inbox of the 基幹店.
  { path: 'notifications', component: () => import('./ReceivedNotificationRoute') },
  { path: KEILOG_OFFICE_DATA_PATH.slice(1), component: () => import('./OfficeDataRoute') },
  { path: KEILOG_INSURER_MASTER_PATH.slice(1), component: () => import('./InsurerMasterRoute') },
  { path: `${KEILOG_INSURER_MASTER_PATH.slice(1)}/create`, component: () => import('./InsurerCreateRoute') },
  { path: `${KEILOG_INSURER_MASTER_PATH.slice(1)}/:id/edit`, component: () => import('./InsurerEditRoute') },
  { path: `${KEILOG_INSURER_MASTER_PATH.slice(1)}/:id/copy`, component: () => import('./InsurerCopyRoute') },
  { path: `${KEILOG_INSURER_MASTER_PATH.slice(1)}/:id`, component: () => import('./InsurerDetailRoute') },
  {
    // SCR-060 施術継続理由・状態記入書 — no menu entry; reached by direct link (SPEC B0).
    path: KEILOG_LONG_TERM_FREQUENTS_PATH.slice(1),
    component: () => import('./LongTermFrequentsRoute'),
  },
  // SCR-148 売上分析 — 統計分析 › 売上分析 for 本部管理 and 本部.
  { path: KEILOG_SALES_ANALYSIS_PATH.slice(1), component: () => import('./SalesAnalysisRoute') },
  { path: KEILOG_ACTIVE_USER_TREND_PATH.slice(1), component: () => import('./ActiveUserTrendRoute') },
  { path: KEILOG_REPORT_DASHBOARD_PATH.slice(1), component: () => import('./ReportDashboardRoute') },
  // SCR-149 売上予測実績値表 (Sales forecast vs actual table): 統計分析 (Statistics analysis) › 売上予測 (Sales forecast) for 本部 (HQ) only.
  { path: KEILOG_SALES_FORECAST_PATH.slice(1), component: () => import('./SalesForecastRoute') },
  // SCR-150 基幹店売上 (Franchise sales): 統計分析 (Statistics analysis) › 基幹店売上 for 本部 (HQ).
  { path: KEILOG_FRANCHISE_SALES_PATH.slice(1), component: () => import('./FranchiseSalesRoute') },
  // SCR-151 売上日計表 (Daily sales): 統計分析 (Statistics) › 売上日計表 for 本部管理 (HQ admin) and 本部 (HQ).
  { path: KEILOG_DAILY_SALES_PATH.slice(1), component: () => import('./DailySalesRoute') },
  // HIT-11538 デザインガイドライン — registered only when the build sets
  // VITE_ENABLE_DESIGN_TEMPLATE=true; see `designGuidelineRoutes.ts`.
  ...keilogDesignGuidelineRoutes,
  /*
   * Must stay LAST: `path: '*'` matches every remaining address of this tenant, and `TenantRouter`
   * spreads `customizeRoutes` BEFORE the core's `path: '*'`, so the KEiLOG 404 page comes before
   * the P1 one. Other tenants still get the P1 page because `TenantScopedRoute` filters by tenant code.
   */
  { path: '*', component: () => import('./NotFoundRoute') },
];

export default keilogClinicRoutes;
